import { XMarkIcon } from "@heroicons/react/24/outline";
import { useMemo } from "react";
import { FeedbackBanner } from "@/core/components/feedback-banner";
import { ScrollArea } from "@/core/components/ui/scroll-area";
import { Separator } from "@/core/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/core/components/ui/sheet";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { useIsMobile } from "@/core/component-hooks/use-mobile";
import { dayjs } from "@/core/utils/date";
import { formatMoney } from "@/core/utils/money";
import { cn } from "@/core/utils/utils";
import type { IcmLineItem } from "@/icm/types";
import { formatTraceValue, humanizeKey } from "@/icm/utils";

export const TechnicianLineItemSheet = ({
  lineItem,
  open,
  onOpenChange,
}: {
  lineItem: IcmLineItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const isMobile = useIsMobile();

  const traceEntries = useMemo(
    () =>
      lineItem
        ? Object.entries(lineItem.calculation_trace).map(([key, value]) => ({
            key,
            label: humanizeKey(key),
            value,
          }))
        : [],
    [lineItem],
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={isMobile ? "bottom" : "right"}
        className={cn("overflow-hidden p-0 [&>button:first-child]:hidden", {
          "rounded-t-2xl": isMobile,
          "h-full w-full sm:max-w-[500px]": !isMobile,
        })}
      >
        {lineItem && (
          <Stack fullWidth gap="none" className="h-full">
            <SheetTitle className="sr-only">
              {lineItem.payout_rule_name}
            </SheetTitle>
            <SheetDescription className="sr-only">
              Line item payout details
            </SheetDescription>
            <Stack gap="lg" className="bg-bg-secondary/50 p-6" fullWidth>
              <Stack direction="row" justify="between" align="start" fullWidth>
                <Stack gap="xs">
                  <Typography variant="titleW3">
                    {lineItem.payout_rule_name}
                  </Typography>
                  {lineItem.customer_name && (
                    <Typography variant="bodyW2" textColor="secondary">
                      {lineItem.customer_name}
                    </Typography>
                  )}
                </Stack>
                <XMarkIcon
                  className="h-6 w-6 shrink-0 cursor-pointer"
                  onClick={() => onOpenChange(false)}
                />
              </Stack>

              <Stack gap="md" fullWidth>
                <Stack direction="row" gap="xl" fullWidth>
                  {lineItem.job_completed_on && (
                    <Stack gap="xs" className="w-[50%]">
                      <Typography variant="subtitleW3" textColor="tertiary">
                        Completed
                      </Typography>
                      <Typography variant="bodyW2">
                        {dayjs(lineItem.job_completed_on).format("MMM D, YYYY")}
                      </Typography>
                    </Stack>
                  )}
                  <Stack gap="xs" className="w-[50%]">
                    <Typography variant="subtitleW3" textColor="tertiary">
                      Payout
                    </Typography>
                    <Typography variant="bodyW2">
                      {formatMoney(lineItem.payout_amount) ?? "-"}
                    </Typography>
                  </Stack>
                </Stack>
                {lineItem.business_unit_name && (
                  <Stack gap="xs">
                    <Typography variant="subtitleW3" textColor="tertiary">
                      Business Unit
                    </Typography>
                    <Typography variant="bodyW2">
                      {lineItem.business_unit_name}
                    </Typography>
                  </Stack>
                )}
                {lineItem.job_type_name && (
                  <Stack gap="xs">
                    <Typography variant="subtitleW3" textColor="tertiary">
                      Job Type
                    </Typography>
                    <Typography variant="bodyW2">
                      {lineItem.job_type_name}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Stack>

            <Separator />

            <div className="w-full flex-1 overflow-hidden bg-card">
              <ScrollArea className="h-full w-full">
                <Stack gap="none" fullWidth className="px-6 pb-16 pt-2">
                  {lineItem.warnings.length > 0 && (
                    <Stack gap="sm" fullWidth className="pb-2 pt-4">
                      {lineItem.warnings.map((warning) => (
                        <FeedbackBanner
                          key={warning}
                          variant="warning"
                          iconSize="sm"
                        >
                          <Typography variant="bodyW2">{warning}</Typography>
                        </FeedbackBanner>
                      ))}
                    </Stack>
                  )}
                  {traceEntries.map((entry) => (
                    <Stack key={entry.key} gap="none" fullWidth>
                      <Stack
                        direction="row"
                        justify="between"
                        align="center"
                        fullWidth
                        className="py-3"
                      >
                        <Typography variant="bodyW2" textColor="secondary">
                          {entry.label}
                        </Typography>
                        <Typography variant="bodyW2">
                          {formatTraceValue(entry.key, entry.value)}
                        </Typography>
                      </Stack>
                      <Separator />
                    </Stack>
                  ))}
                </Stack>
              </ScrollArea>
            </div>
          </Stack>
        )}
      </SheetContent>
    </Sheet>
  );
};
